import { useEffect } from 'react';

import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import styled, { css } from 'styled-components';

import { Field } from '@components/utils/form';
import ErrorMessages from '@components/ui/errors/ErrorMessages';
import { TextAreaBox } from '@components/ui/form/common/styled';
import Button from '@components/Button';
import { Row, Col, FlexibleDiv } from '@components/formStyles';
import { RadioGroup } from '@components/RadioGroup';
import { PhoneNumber } from '@components/ui/form/FormattedInputs';
import { ContactType, ContactTime, ErrorViewModel } from '@api/index';
import Modal from '@components/Modal';
import { DateType, formatMomentDate } from '@utils/dateUtil';
import { AlertType } from '@components/views/hotel/detail/components/room-selection/components/Alert';
import PersonName from '@components/ui/form/PersonName';
import Input from '@components/ui/form/Input';
import { NameValidation } from '@constants/constants';

const DEFAULT_COUNTRY_CODE = '+90';

import { LetUsCallYouFormData } from './types';

const AvailableTimeData: { value: ContactTime; label: string }[] = [
  {
    value: ContactTime.MORNING,
    label: 'Sabah',
  },
  {
    value: ContactTime.NOON,
    label: 'Öğlen',
  },
  {
    value: ContactTime.EVENING,
    label: 'Akşam',
  },
];

// const ContactOptionData: { value: ContactType; label: string }[] = [
//   {
//     value: ContactType.PHONE,
//     label: 'Telefon',
//   },
//   {
//     value: ContactType.EMAIL,
//     label: 'E-Mail',
//   },
// ];

const schema = Yup.object().shape({
  firstName: Yup.string()
    .max(
      NameValidation.MAX_NAME_INPUT_LENGTH,
      NameValidation.MAX_NAME_INPUT_LENGTH_ERROR_MESSAGE
    )
    .required('Zorunlu Alan'),
  lastName: Yup.string()
    .max(
      NameValidation.MAX_NAME_INPUT_LENGTH,
      NameValidation.MAX_NAME_INPUT_LENGTH_ERROR_MESSAGE
    )
    .required('Zorunlu Alan'),
  contactOption: Yup.string()
    .oneOf([ContactType.EMAIL, ContactType.PHONE])
    .required('Zorunlu Alan'),
  phone: Yup.string().required('Lütfen geçerli bir telefonu numarası giriniz.'),
  email: Yup.string()
    .email('Lütfen geçerli bir e-posta giriniz.')
    .max(255)
    .required('E-posta alanı boş bırakılamaz.'),
  availableTime: Yup.string().oneOf(
    AvailableTimeData.map(({ value }) => value)
  ),
  message: Yup.string(),
});

const Header = styled.h1`
  font-size: 24px;
  font-weight: 600;
  color: ${({ theme }) => theme.palette.darkGrey};
`;

const infoTextMixin = css`
  font-size: 16px;
  font-weight: normal;
  line-height: 1.5;
  color: ${({ theme }) => theme.palette.matterHorn};
`;

const InfoText = styled.p`
  ${infoTextMixin}
  padding: 0;
  margin: 0;
`;

const InfoBoldText = styled.span`
  ${infoTextMixin}
  font-weight: bold;
`;

const RequiredWarningText = styled.div`
  opacity: 0.75;
  font-size: 16px;
  line-height: 1.5;
  letter-spacing: normal;
  color: ${({ theme }) => theme.palette.matterHorn};
`;

const SubHeader = styled.div`
  margin-top: 15px;
  font-size: 18px;
  font-weight: 600;
  color: ${({ theme }) => theme.palette.darkGrey};
`;

const FieldWrapper = styled.div<{ $hasError?: boolean }>`
  margin-bottom: ${(p) => (p.$hasError ? '24px' : '0')};
`;

const PhoneFieldWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CountryCodeBox = styled.div<{ $hasError?: boolean }>`
  display: flex;
  align-items: center;
  height: 42px;
  padding: 0 14px;
  border: 1px solid
    ${({ theme, $hasError }) =>
      $hasError ? theme.palette.errorColor : theme.palette.grey200};
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  white-space: nowrap;
  color: ${({ theme }) => theme.palette.darkGrey};
`;

const PhoneNumberField = styled.div`
  flex: 1;

  input {
    font-size: 14px;
    font-weight: 500;
    padding-left: 12px;
    &::placeholder {
      color: ${({ theme }) => theme.palette.inputPlaceholderColor};
    }
  }
`;

const NameCol = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-column-gap: 20px;
  @media ${({ theme }) => `(max-width: ${theme.breakPoint.LARGE_1X})`} {
    grid-template-columns: 1fr;
    grid-row-gap: 20px;
  }
`;

const Wrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-row-gap: 20px;
`;

const StyledModal = styled(Modal)`
  &&& {
    &.setur-modal {
      max-width: 100vw;
      margin: 0 auto;
      padding-bottom: 0;
    }

    .setur-modal-content {
      height: auto;
      max-height: calc(100vh - 48px);
      max-height: calc(100dvh - 48px);
      overflow-y: auto;
    }

    .setur-modal-title {
      margin-bottom: 20px;
    }

    @media ${({ theme }) => `(max-width: ${theme.breakPoint.LARGE_1X})`} {
      &.setur-modal {
        width: 100% !important;
        max-width: 100vw;
        min-height: 0;
        margin: 0;
        padding-bottom: 0;
        top: 0;
      }

      .setur-modal-content {
        height: 100vh;
        height: 100dvh;
        min-height: 100vh;
        min-height: 100dvh;
        max-height: 100vh;
        max-height: 100dvh;
        overflow-y: auto;
      }

      .setur-modal-title {
        margin-bottom: 0;
      }
    }
  }
`;

const ApplyButton = styled(Button)`
  height: 58px !important;
  font-weight: bold;
`;

const LetUsCallYouModal = ({
  onSubmit,
  onCancel,
  entityName,
  formCheckInDate,
  formCheckOutDate,
  isPostCallMeLoading,
  isPostCallMeError,
  alertType = AlertType.PRIMARY,
}: {
  onSubmit: (formData: LetUsCallYouFormData) => void;
  onCancel?: () => void;
  entityName: string;
  formCheckInDate: DateType | null;
  formCheckOutDate: DateType | null;
  isPostCallMeLoading: boolean;
  alertType?: AlertType;
  isPostCallMeError?: ErrorViewModel[];
}) => {
  const hasCallMeError = isPostCallMeError !== undefined;
  const callMeErrors = Array.isArray(isPostCallMeError)
    ? isPostCallMeError
    : [];
  const callMeErrorMessages = callMeErrors
    .filter((error) => Boolean(error.detail?.trim()))
    .map((error, index) => ({
      ...error,
      title: `call-me-error-${index}`,
    }));
  const displayedCallMeErrors = callMeErrorMessages.length
    ? callMeErrorMessages
    : hasCallMeError
    ? [
        {
          title: 'call-me-error-fallback',
          detail: 'İşlem sırasında bir hata oluştu. Lütfen tekrar deneyiniz.',
        },
      ]
    : [];

  const {
    handleSubmit,
    control,
    getValues,
    formState: { errors },
  } = useForm<LetUsCallYouFormData>({
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      availableTime: undefined,
      contactOption: ContactType.PHONE,
      message: '',
    },
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    const body = document.querySelector('body');
    if (body) {
      body.style.overflow = 'hidden';
      return () => {
        setTimeout(() => {
          body.style.overflow = 'initial';
        }, 0);
      };
    }
  }, []);

  return (
    <StyledModal
      width={540}
      title={
        <Header>
          {alertType === AlertType.PRIMARY
            ? 'Biz Sizi Arayalım'
            : 'Kontenjan Talep Et'}
        </Header>
      }
      isVisible={true}
      onCancel={onCancel}
    >
      <Wrapper>
        <InfoText>
          <InfoBoldText>
            {entityName} {formatMomentDate(formCheckInDate)}
            {formCheckOutDate ? ` - ${formatMomentDate(formCheckOutDate)}` : ''}
          </InfoBoldText>
          &nbsp;
          {alertType === AlertType.PRIMARY
            ? 'müsaitliği hakkında bilgi almak için aşağıdaki formu doldurabilirsiniz.'
            : 'kontenjan talep etmek için aşağıdaki formu doldurabilirsiniz.'}
        </InfoText>

        <RequiredWarningText>
          (*) Doldurulması zorunlu alanlar.
        </RequiredWarningText>
        {displayedCallMeErrors.length ? (
          <ErrorMessages errors={displayedCallMeErrors} />
        ) : null}
        <form onSubmit={handleSubmit(onSubmit)}>
          <Wrapper>
            <NameCol>
              <Controller
                name='firstName'
                key='firstName'
                control={control}
                render={({ field }) => (
                  <Field
                    Component={PersonName}
                    errorMessage={errors.firstName?.message}
                    hasError={!!errors.firstName?.message}
                    id='firstName'
                    label='Ad*'
                    placeholder='Giriniz'
                    {...field}
                  />
                )}
              />
              <Controller
                key='lastName'
                name='lastName'
                control={control}
                render={({ field }) => (
                  <Field
                    Component={PersonName}
                    errorMessage={errors.lastName?.message}
                    hasError={!!errors.lastName?.message}
                    id='lastName'
                    label='Soyad*'
                    placeholder='Giriniz'
                    {...field}
                  />
                )}
              />
            </NameCol>
            <Row>
              <Col>
                <SubHeader>Size Ne Zaman Ulaşalım?</SubHeader>
                <FlexibleDiv top={10} />
                <Controller
                  key='availableTime'
                  name='availableTime'
                  control={control}
                  render={({ field }) => (
                    <Field
                      Component={(props) => (
                        <RadioGroup
                          options={AvailableTimeData}
                          getValue={(option) => option.value}
                          getLabel={(option) => option.label}
                          direction='column'
                          selectedIndex={AvailableTimeData.findIndex(
                            (d) => d.value === getValues('availableTime')
                          )}
                          {...props}
                        />
                      )}
                      {...field}
                    />
                  )}
                />
              </Col>
            </Row>

            <Controller
              key='email'
              name='email'
              control={control}
              render={({ field }) => (
                <FieldWrapper $hasError={!!errors.email?.message}>
                  <Field
                    Component={Input}
                    errorMessage={errors.email?.message}
                    hasError={!!errors.email?.message}
                    id='email'
                    label='E-posta*'
                    placeholder='Giriniz'
                    {...field}
                  />
                </FieldWrapper>
              )}
            />

            <Controller
              key='phone'
              name='phone'
              control={control}
              render={({ field }) => (
                <FieldWrapper $hasError={!!errors.phone?.message}>
                  <Field
                    Component={(props: { hasError?: boolean }) => (
                      <div>
                        <PhoneFieldWrapper>
                          <CountryCodeBox $hasError={props.hasError}>
                            {DEFAULT_COUNTRY_CODE}
                          </CountryCodeBox>
                          <PhoneNumberField>
                            <PhoneNumber
                              {...props}
                              placeholder='Başında sıfır olmadan giriş yapınız.'
                            />
                          </PhoneNumberField>
                        </PhoneFieldWrapper>
                      </div>
                    )}
                    errorMessage={errors.phone?.message}
                    hasError={!!errors.phone?.message}
                    label='Telefon*'
                    {...field}
                  />
                </FieldWrapper>
              )}
            />
            <Controller
              key='message'
              name='message'
              control={control}
              render={({ field }) => (
                <Field
                  Component={TextAreaBox}
                  placeholder='Mesajınız (Opsiyonel)'
                  rows={5}
                  {...field}
                />
              )}
            />

            <Row justifyContent='center'>
              <ApplyButton
                block
                loading={isPostCallMeLoading}
                disabled={isPostCallMeLoading}
                htmlType='submit'
              >
                Gönder
              </ApplyButton>
            </Row>
          </Wrapper>
        </form>
      </Wrapper>
    </StyledModal>
  );
};

export default LetUsCallYouModal;
